import { useCallback, useEffect, useRef, useState } from 'react';
import { BeakerIcon, ArrowDownTrayIcon, ClipboardDocumentIcon } from '@heroicons/react/24/outline';
import { geneticsApi, type MutationLocus } from '../../api/genetics';
import { Gender } from '../../utils/enums';
import { subscribeGeneticsCatalogChanged } from '../../utils/geneticsCatalogEvents';
import {
  allelesToStatus,
  buildGeneticsSummary,
  getLocusStatusOptions,
  statusToAlleles,
  type GeneticsSummary,
  type LocusGenotypeStatus,
  type PendingGenotype,
} from '../../utils/genotypeEditorUtils';
import TestBreedingHint from './TestBreedingHint';

interface BirdGenotypeEditorProps {
  speciesId?: string;
  birdId?: string;
  gender: Gender;
  /** Pending genotypes for pre-save flows (e.g. banding). */
  value?: PendingGenotype[];
  onChange?: (genotypes: PendingGenotype[], summary: GeneticsSummary) => void;
  /** Persist immediately when birdId is set. */
  autoSave?: boolean;
  compact?: boolean;
  showImportLegacy?: boolean;
  onImported?: () => void;
}

function statusesToGenotypes(
  locusList: MutationLocus[],
  nextStatus: Record<string, LocusGenotypeStatus>,
  gender: Gender,
): PendingGenotype[] {
  const genotypes: PendingGenotype[] = [];
  for (const locus of locusList) {
    const status = nextStatus[locus.id] ?? 'wild';
    if (status === 'wild') continue;
    genotypes.push({
      locusId: locus.id,
      alleles: statusToAlleles(locus, status, gender),
    });
  }
  return genotypes;
}

export default function BirdGenotypeEditor({
  speciesId,
  birdId,
  gender,
  value,
  onChange,
  autoSave = !!birdId,
  compact = false,
  showImportLegacy = !!birdId,
  onImported,
}: BirdGenotypeEditorProps) {
  const [loci, setLoci] = useState<MutationLocus[]>([]);
  const [statusByLocus, setStatusByLocus] = useState<Record<string, LocusGenotypeStatus>>({});
  const [loading, setLoading] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importingCode, setImportingCode] = useState(false);
  const [geneticCode, setGeneticCode] = useState('');
  const [showGeneticCodeImport, setShowGeneticCodeImport] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [summary, setSummary] = useState<GeneticsSummary | null>(null);
  const [coi, setCoi] = useState<{ percent: number; message?: string } | null>(null);
  const [reloadToken, setReloadToken] = useState(0);

  const onChangeRef = useRef(onChange);
  const valueRef = useRef(value);
  const genderRef = useRef(gender);
  onChangeRef.current = onChange;
  valueRef.current = value;
  genderRef.current = gender;

  const emitChange = useCallback(
    (nextStatus: Record<string, LocusGenotypeStatus>, locusList: MutationLocus[]) => {
      const genotypes = statusesToGenotypes(locusList, nextStatus, gender);
      const nextSummary = buildGeneticsSummary(locusList, genotypes, gender);
      setSummary(nextSummary);
      onChangeRef.current?.(genotypes, nextSummary);
      return { genotypes, nextSummary };
    },
    [gender],
  );

  useEffect(() => {
    let cancelled = false;

    const loadCatalog = async () => {
      if (!speciesId) {
        setLoci([]);
        setStatusByLocus({});
        setSummary(null);
        setLoading(false);
        setError(null);
        return;
      }

      setLoading(true);
      setError(null);
      try {
        await geneticsApi.seedSpecies(speciesId);
        const catalog = await geneticsApi.getSpeciesLoci(speciesId);
        if (cancelled) return;

        const initialStatus: Record<string, LocusGenotypeStatus> = {};
        for (const locus of catalog) {
          initialStatus[locus.id] = 'wild';
        }

        const currentGender = genderRef.current;
        if (birdId) {
          const stored = await geneticsApi.getBirdGenotypes(birdId);
          if (cancelled) return;
          for (const record of stored) {
            if (!record.locusId) continue;
            const locus = catalog.find((l) => l.id === record.locusId);
            if (locus) {
              initialStatus[locus.id] = allelesToStatus(locus, record.alleles, currentGender);
            }
          }
        } else if (valueRef.current?.length) {
          for (const entry of valueRef.current) {
            const locus = catalog.find((l) => l.id === entry.locusId);
            if (locus) {
              initialStatus[locus.id] = allelesToStatus(locus, entry.alleles, currentGender);
            }
          }
        }

        setLoci(catalog);
        setStatusByLocus(initialStatus);
        const genotypes = statusesToGenotypes(catalog, initialStatus, currentGender);
        const nextSummary = buildGeneticsSummary(catalog, genotypes, currentGender);
        setSummary(nextSummary);
        onChangeRef.current?.(genotypes, nextSummary);
      } catch (err: unknown) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Failed to load mutation catalog');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    void loadCatalog();
    return () => {
      cancelled = true;
    };
  }, [speciesId, birdId, reloadToken]);

  const reloadCatalog = useCallback(() => {
    setReloadToken((token) => token + 1);
  }, []);

  useEffect(() => subscribeGeneticsCatalogChanged(speciesId, reloadCatalog), [speciesId, reloadCatalog]);

  useEffect(() => {
    if (!birdId) {
      setCoi(null);
      return;
    }
    geneticsApi
      .getBirdCoi(birdId)
      .then((result) => setCoi({ percent: result.percent, message: result.message }))
      .catch(() => setCoi(null));
  }, [birdId]);

  useEffect(() => {
    if (loci.length) {
      emitChange(statusByLocus, loci);
    }
  }, [gender]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleStatusChange = async (locus: MutationLocus, status: LocusGenotypeStatus) => {
    const nextStatus = { ...statusByLocus, [locus.id]: status };
    setStatusByLocus(nextStatus);
    const { genotypes } = emitChange(nextStatus, loci);

    if (autoSave && birdId) {
      try {
        await geneticsApi.syncBirdGenotypes(birdId, { genotypes });
      } catch (err: unknown) {
        setError(err instanceof Error ? err.message : 'Failed to save genotype');
      }
    }
  };

  const handleImportLegacy = async () => {
    if (!birdId) return;
    setImporting(true);
    setError(null);
    try {
      const result = await geneticsApi.importLegacyGenotypes(birdId);
      if (result.imported > 0) {
        reloadCatalog();
        onImported?.();
      } else {
        setError('No matching loci found in legacy mutation/genotype text.');
      }
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Import failed');
    } finally {
      setImporting(false);
    }
  };

  const handleImportGeneticCode = async () => {
    if (!birdId || !geneticCode.trim()) return;
    setImportingCode(true);
    setError(null);
    try {
      const result = await geneticsApi.importGeneticCode(birdId, geneticCode.trim());
      if (result.imported > 0) {
        setGeneticCode('');
        setShowGeneticCodeImport(false);
        reloadCatalog();
        onImported?.();
      } else {
        const hint =
          result.unmatchedTokens.length > 0
            ? ` Unrecognized tokens: ${result.unmatchedTokens.join(', ')}`
            : '';
        setError(`No matching loci found in genetic code.${hint}`);
      }
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Genetic code import failed');
    } finally {
      setImportingCode(false);
    }
  };

  if (!speciesId) {
    return (
      <p className="text-sm text-slate-500">Select a species to record structured genotypes.</p>
    );
  }

  if (loading) {
    return <p className="text-sm text-slate-500">Loading mutation catalog…</p>;
  }

  if (error) {
    return <p className="text-sm text-red-600">{error}</p>;
  }

  if (loci.length === 0) {
    return (
      <p className="text-sm text-slate-500">
        No mutation catalog is available for this species yet.
      </p>
    );
  }

  return (
    <div className={compact ? 'space-y-3' : 'space-y-4'}>
      <div className="flex items-center justify-between gap-2">
        {!compact ? (
          <div className="flex items-center gap-2 text-sm text-slate-600">
            <BeakerIcon className="h-4 w-4 text-indigo-600" />
            <span>
              Record genotype at each locus. Status options depend on inheritance mode and sex.
            </span>
          </div>
        ) : (
          <span />
        )}
        {showImportLegacy && birdId && (
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => setShowGeneticCodeImport((v) => !v)}
              disabled={importing || loading}
              className="inline-flex items-center gap-1 text-xs text-indigo-700 hover:text-indigo-900 underline disabled:opacity-50"
            >
              <ClipboardDocumentIcon className="h-3.5 w-3.5" />
              {showGeneticCodeImport ? 'Hide genetic code' : 'Paste genetic code'}
            </button>
            <button
              type="button"
              onClick={handleImportLegacy}
              disabled={importing || loading}
              className="inline-flex items-center gap-1 text-xs text-indigo-700 hover:text-indigo-900 underline disabled:opacity-50"
            >
              <ArrowDownTrayIcon className="h-3.5 w-3.5" />
              {importing ? 'Importing…' : 'Import legacy text'}
            </button>
          </div>
        )}
      </div>

      {showGeneticCodeImport && birdId ? (
        <div className="rounded-md border border-indigo-200 bg-indigo-50/50 p-3 space-y-2">
          <p className="text-xs text-slate-600">
            Paste GenCalc-style genetic code (e.g. <span className="font-mono">bl+/bl ino/ino</span>).
            Only loci in the species catalog are applied; other loci are left unchanged.
          </p>
          <textarea
            value={geneticCode}
            onChange={(e) => setGeneticCode(e.target.value)}
            rows={2}
            placeholder="bl+/bl ino/ino"
            className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 text-sm font-mono"
          />
          <button
            type="button"
            onClick={handleImportGeneticCode}
            disabled={importingCode || !geneticCode.trim()}
            className="inline-flex items-center rounded-md bg-indigo-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-indigo-700 disabled:opacity-50"
          >
            {importingCode ? 'Importing…' : 'Import genetic code'}
          </button>
        </div>
      ) : null}

      {coi && birdId ? (
        <p className="text-xs text-slate-600 bg-slate-50 border border-slate-200 rounded px-3 py-2">
          Inbreeding coefficient (COI): <span className="font-medium">{coi.percent}%</span>
          {coi.message ? ` — ${coi.message}` : null}
        </p>
      ) : null}

      <div className="space-y-3">
        {loci.map((locus) => {
          const options = getLocusStatusOptions(locus, gender);
          const current = statusByLocus[locus.id] ?? 'wild';
          return (
            <div
              key={locus.id}
              className="rounded-md border border-slate-200 bg-white p-3"
            >
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                <div>
                  <span className="font-medium text-slate-900">{locus.locusName}</span>
                  <span className="ml-2 font-mono text-xs text-slate-500">({locus.locusSymbol})</span>
                  <p className="text-xs text-slate-500 mt-0.5">
                    {locus.inheritanceMode.replace(/_/g, ' ').toLowerCase()}
                    {locus.chromosome === 'Z' ? ' · sex-linked' : ''}
                  </p>
                </div>
                <select
                  value={current}
                  onChange={(e) => handleStatusChange(locus, e.target.value as LocusGenotypeStatus)}
                  className="block w-full sm:w-56 rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 text-sm"
                >
                  {options.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>
              {birdId &&
              gender === Gender.MALE &&
              (current === 'split' || current.startsWith('split_')) ? (
                <TestBreedingHint birdId={birdId} locusId={locus.id} />
              ) : null}
            </div>
          );
        })}
      </div>

      {summary && (summary.mutationName || summary.genotype) ? (
        <div className="rounded-md bg-indigo-50 border border-indigo-100 p-3 text-sm">
          <p className="font-medium text-indigo-900 mb-1">Derived summary</p>
          {summary.mutationName ? (
            <p className="text-slate-700">
              <span className="text-slate-500">Mutations:</span> {summary.mutationName}
            </p>
          ) : null}
          {summary.phenotype ? (
            <p className="text-slate-700">
              <span className="text-slate-500">Phenotype:</span> {summary.phenotype}
            </p>
          ) : null}
          {summary.genotype ? (
            <p className="text-slate-700 font-mono text-xs mt-1">{summary.genotype}</p>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
